/**
 * device/apps/pulse.ts — Consciousness Dashboard
 *
 * The Pulse app makes growth visible. Combat stats with level badge, HP/XP
 * bars, equipment loadout in a visual grid, then the eight-skill radar chart
 * with XP bars and mastery tiers — all the invisible numbers that shape
 * your world, rendered as beautiful data.
 *
 * This is the "stats" app with soul.
 */

import type { GameState, InventoryItem, EquipmentSlot } from "../../engine/state.js";
import type { DeviceApp, Rect } from "../app.js";
import { registerApp, drawRoundRect, drawText } from "../app.js";
import {
  getMomentumState, getFamiliarity, getAttunementData, getCombatAwareness,
} from "../../combat/system.js";

// ─── Skill Metadata ───────────────────────────────────────────────────────

interface SkillMeta {
  key: string;
  label: string;
  color: string;
  icon: string;  // single character
}

const SKILLS: SkillMeta[] = [
  { key: "crafting",    label: "Crafting",    color: "#f59e0b", icon: "\u2692" },
  { key: "cooking",     label: "Cooking",     color: "#ef4444", icon: "\u2615" },
  { key: "gardening",   label: "Gardening",   color: "#22c55e", icon: "\u2618" },
  { key: "music",       label: "Music",       color: "#8b5cf6", icon: "\u266B" },
  { key: "art",         label: "Art",         color: "#ec4899", icon: "\u2605" },
  { key: "design",      label: "Design",      color: "#06b6d4", icon: "\u25C6" },
  { key: "fishing",     label: "Fishing",     color: "#3b82f6", icon: "\u2248" },
  { key: "exploration", label: "Explore",     color: "#f97316", icon: "\u2316" },
];

/** XP required for a given level (quadratic curve) */
function xpForLevel(level: number): number {
  return Math.round(5 * level * level);
}

/** Derive level from raw XP */
function levelFromXP(xp: number): number {
  return Math.floor(Math.sqrt(xp / 5));
}

/** Mastery tier name */
function masteryTier(level: number): string {
  if (level >= 80) return "Master";
  if (level >= 60) return "Artisan";
  if (level >= 40) return "Practitioner";
  if (level >= 20) return "Apprentice";
  return "Novice";
}

// ─── Rarity Colors ──────────────────────────────────────────────────────────

const RARITY_COLORS: Record<string, string> = {
  common:    "#aaaaaa",
  uncommon:  "#4a9a40",
  rare:      "#3a7acc",
  legendary: "#c8a030",
  seasonal:  "#cc5577",
};

// ─── Equipment Slot Display Config ──────────────────────────────────────────

interface SlotDisplay {
  key: EquipmentSlot;
  label: string;
  icon: string; // drawn procedurally, but this is a fallback label char
}

const EQUIPMENT_SLOTS: SlotDisplay[] = [
  { key: "weapon",    label: "Weapon",    icon: "\u2694" },
  { key: "shield",    label: "Shield",    icon: "\u26E8" },
  { key: "armor",     label: "Armor",     icon: "\u26CF" },
  { key: "accessory", label: "Accessory", icon: "\u25C7" },
];

// ─── Scroll State ───────────────────────────────────────────────────────────

interface PulseState {
  scrollY: number;
  scrollVelocity: number;
  maxScrollY: number;
  /** Which equipment slot index is expanded (-1 = none) */
  expandedSlot: number;
}

function getState(state: GameState): PulseState {
  if (!state.player.device.appStates["pulse"]) {
    state.player.device.appStates["pulse"] = {
      scrollY: 0,
      scrollVelocity: 0,
      maxScrollY: 0,
      expandedSlot: -1,
    } satisfies PulseState;
  }
  return state.player.device.appStates["pulse"] as PulseState;
}

// ─── Equipment Bonus Computation ────────────────────────────────────────────

function computeEquipBonuses(state: GameState): { attack: number; defense: number; hp: number } {
  const eq = state.player.equipment;
  let attack = 0, defense = 0, hp = 0;
  const slots: EquipmentSlot[] = ["weapon", "armor", "accessory", "shield"];
  for (const slot of slots) {
    const item = eq[slot];
    if (item?.equipmentData) {
      attack += item.equipmentData.attackBonus;
      defense += item.equipmentData.defenseBonus;
      hp += item.equipmentData.hpBonus;
    }
  }
  return { attack, defense, hp };
}

// ─── Equipment Icon Drawing ─────────────────────────────────────────────────

function drawEquipmentIcon(
  ctx: CanvasRenderingContext2D,
  slot: EquipmentSlot,
  cx: number, cy: number, size: number,
  filled: boolean
) {
  const s = size * 0.4;
  ctx.lineWidth = filled ? 1.5 : 1;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  if (slot === "weapon") {
    // Sword — blade + crossguard + grip
    ctx.beginPath();
    ctx.moveTo(cx, cy - s);        // tip
    ctx.lineTo(cx, cy + s * 0.4);  // blade end
    ctx.stroke();
    // Crossguard
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.45, cy + s * 0.1);
    ctx.lineTo(cx + s * 0.45, cy + s * 0.1);
    ctx.stroke();
    // Grip
    ctx.beginPath();
    ctx.moveTo(cx, cy + s * 0.4);
    ctx.lineTo(cx, cy + s * 0.8);
    ctx.stroke();
    // Pommel
    ctx.beginPath();
    ctx.arc(cx, cy + s * 0.85, 1.2, 0, Math.PI * 2);
    ctx.fill();
  } else if (slot === "shield") {
    // Shield — rounded pentagon shape
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.5, cy - s * 0.5);
    ctx.lineTo(cx + s * 0.5, cy - s * 0.5);
    ctx.lineTo(cx + s * 0.55, cy + s * 0.1);
    ctx.lineTo(cx, cy + s * 0.7);
    ctx.lineTo(cx - s * 0.55, cy + s * 0.1);
    ctx.closePath();
    if (filled) ctx.fill();
    ctx.stroke();
  } else if (slot === "armor") {
    // Chestplate — vest shape
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.5, cy - s * 0.5);
    ctx.lineTo(cx - s * 0.15, cy - s * 0.65);
    ctx.lineTo(cx + s * 0.15, cy - s * 0.65);
    ctx.lineTo(cx + s * 0.5, cy - s * 0.5);
    ctx.lineTo(cx + s * 0.45, cy + s * 0.5);
    ctx.lineTo(cx + s * 0.15, cy + s * 0.65);
    ctx.lineTo(cx - s * 0.15, cy + s * 0.65);
    ctx.lineTo(cx - s * 0.45, cy + s * 0.5);
    ctx.closePath();
    if (filled) ctx.fill();
    ctx.stroke();
  } else {
    // Ring — accessory
    ctx.beginPath();
    ctx.arc(cx, cy, s * 0.45, 0, Math.PI * 2);
    if (filled) ctx.fill();
    ctx.stroke();
    // Gem on top
    ctx.beginPath();
    ctx.moveTo(cx, cy - s * 0.65);
    ctx.lineTo(cx - s * 0.2, cy - s * 0.45);
    ctx.lineTo(cx + s * 0.2, cy - s * 0.45);
    ctx.closePath();
    ctx.fill();
  }
}

// ─── Pulse App ────────────────────────────────────────────────────────────

const pulseApp: DeviceApp = {
  id: "pulse",
  name: "Pulse",
  iconGradient: ["#7c3aed", "#4f46e5"],

  drawIcon(ctx, cx, cy, size) {
    // Heartbeat line
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const s = size * 0.3;
    ctx.beginPath();
    ctx.moveTo(cx - s, cy + 1);
    ctx.lineTo(cx - s * 0.5, cy + 1);
    ctx.lineTo(cx - s * 0.25, cy - s * 0.6);
    ctx.lineTo(cx + s * 0.1, cy + s * 0.5);
    ctx.lineTo(cx + s * 0.35, cy - s * 0.3);
    ctx.lineTo(cx + s * 0.5, cy + 1);
    ctx.lineTo(cx + s, cy + 1);
    ctx.stroke();
  },

  drawContent(ctx, rect, state, dt) {
    const { x, y, w, h } = rect;
    const ps = getState(state);

    // Update inertia scroll
    if (Math.abs(ps.scrollVelocity) > 0.5) {
      ps.scrollY += ps.scrollVelocity * dt;
      ps.scrollVelocity *= 0.92; // friction
    } else {
      ps.scrollVelocity = 0;
    }
    ps.scrollY = Math.max(0, Math.min(ps.scrollY, ps.maxScrollY));

    // Clip to content area
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();

    const margin = 8;
    const innerW = w - margin * 2;
    let curY = y + 4 - ps.scrollY;

    // ── Section: Combat Stats ─────────────────────────────────────

    const combat = state.player.combat;
    const bonuses = computeEquipBonuses(state);

    // Section header
    curY += 2;
    drawSectionHeader(ctx, x + margin, curY, innerW, "COMBAT");
    curY += 16;

    // Level badge + HP/XP bars row
    const badgeSize = 32;
    const badgeX = x + margin + 4;
    const barsX = badgeX + badgeSize + 10;
    const barsW = innerW - badgeSize - 18;

    // ── Level Badge (gold circle) ──
    const badgeCy = curY + badgeSize / 2;
    // Outer ring
    const badgeGrad = ctx.createRadialGradient(
      badgeX + badgeSize / 2, badgeCy, badgeSize * 0.25,
      badgeX + badgeSize / 2, badgeCy, badgeSize * 0.5
    );
    badgeGrad.addColorStop(0, "#fcd34d");
    badgeGrad.addColorStop(1, "#b45309");
    ctx.fillStyle = badgeGrad;
    ctx.beginPath();
    ctx.arc(badgeX + badgeSize / 2, badgeCy, badgeSize / 2, 0, Math.PI * 2);
    ctx.fill();
    // Inner circle
    ctx.fillStyle = "#1c1917";
    ctx.beginPath();
    ctx.arc(badgeX + badgeSize / 2, badgeCy, badgeSize / 2 - 2.5, 0, Math.PI * 2);
    ctx.fill();
    // Level text
    ctx.font = "bold 10px sans-serif";
    ctx.fillStyle = "#fcd34d";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${combat.level}`, badgeX + badgeSize / 2, badgeCy - 2);
    ctx.font = "5px sans-serif";
    ctx.fillStyle = "rgba(252,211,77,0.7)";
    ctx.fillText("LVL", badgeX + badgeSize / 2, badgeCy + 8);

    // ── HP Bar ──
    const hpBarH = 10;
    const hpBarY = curY + 2;
    const hpRatio = combat.maxHp > 0 ? combat.hp / combat.maxHp : 0;
    // Background
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    drawRoundRect(ctx, barsX, hpBarY, barsW, hpBarH, hpBarH / 2);
    ctx.fill();
    // Fill — green→yellow→red based on ratio
    if (hpRatio > 0) {
      const hpFillW = Math.max(hpBarH, barsW * hpRatio);
      let hpColor: string;
      if (hpRatio > 0.6) hpColor = "#22c55e";
      else if (hpRatio > 0.3) hpColor = "#eab308";
      else hpColor = "#ef4444";
      const hpGrad = ctx.createLinearGradient(barsX, 0, barsX + hpFillW, 0);
      hpGrad.addColorStop(0, hpColor);
      hpGrad.addColorStop(1, adjustAlpha(hpColor, 0.7));
      ctx.fillStyle = hpGrad;
      drawRoundRect(ctx, barsX, hpBarY, hpFillW, hpBarH, hpBarH / 2);
      ctx.fill();
    }
    // HP text
    ctx.font = "bold 6px sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${combat.hp}/${combat.maxHp + bonuses.hp} HP`, barsX + barsW / 2, hpBarY + hpBarH / 2);

    // ── XP Bar ──
    const xpBarH = 7;
    const xpBarY = hpBarY + hpBarH + 4;
    const xpRatio = combat.xpToNext > 0 ? combat.xp / combat.xpToNext : 0;
    // Background
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    drawRoundRect(ctx, barsX, xpBarY, barsW, xpBarH, xpBarH / 2);
    ctx.fill();
    // Fill — gold
    if (xpRatio > 0) {
      const xpFillW = Math.max(xpBarH, barsW * xpRatio);
      const xpGrad = ctx.createLinearGradient(barsX, 0, barsX + xpFillW, 0);
      xpGrad.addColorStop(0, "#fbbf24");
      xpGrad.addColorStop(1, "#d97706");
      ctx.fillStyle = xpGrad;
      drawRoundRect(ctx, barsX, xpBarY, xpFillW, xpBarH, xpBarH / 2);
      ctx.fill();
    }
    // XP text
    ctx.font = "bold 5px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.8)";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${combat.xp}/${combat.xpToNext} XP`, barsX + barsW / 2, xpBarY + xpBarH / 2);

    curY += badgeSize + 6;

    // ── Stat Rows: Attack & Defense ──
    const statRowH = 16;
    const statLabelX = x + margin + 6;
    const statValX = x + w - margin - 6;

    // Attack row
    drawStatRow(ctx, statLabelX, curY, statValX,
      "\u2694\uFE0F", "Attack", combat.attack, bonuses.attack, "#f59e0b");
    curY += statRowH;

    // Defense row
    drawStatRow(ctx, statLabelX, curY, statValX,
      "\uD83D\uDEE1\uFE0F", "Defense", combat.defense, bonuses.defense, "#60a5fa");
    curY += statRowH + 4;

    // Divider
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(x + margin, curY);
    ctx.lineTo(x + w - margin, curY);
    ctx.stroke();
    curY += 6;

    // ── Section: Living Combat ───────────────────────────────────
    // Surfaces the dynamical combat systems from combat/dynamics.ts —
    // momentum, flow state, fatigue, combat style signature, species
    // familiarity, and weapon attunement. This is the "quiet mode"
    // deep inspection layer where the player can read the shape of
    // their own fighting from the inside.
    curY = drawLivingCombatSection(ctx, x + margin, curY, innerW, state);

    // Divider
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(x + margin, curY);
    ctx.lineTo(x + w - margin, curY);
    ctx.stroke();
    curY += 6;

    // ── Section: Equipment ────────────────────────────────────────

    drawSectionHeader(ctx, x + margin, curY, innerW, "EQUIPMENT");
    curY += 16;

    // 2x2 grid of equipment slots
    const slotSize = Math.floor((innerW - 6) / 2);
    const slotH = 36;
    const slotGap = 6;

    for (let i = 0; i < 4; i++) {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const sx = x + margin + col * (slotSize + slotGap);
      const sy = curY + row * (slotH + slotGap);
      const slotDef = EQUIPMENT_SLOTS[i];
      const item = state.player.equipment[slotDef.key];

      drawEquipSlot(ctx, sx, sy, slotSize, slotH, slotDef, item, ps.expandedSlot === i);
    }
    curY += 2 * (slotH + slotGap);

    // Expanded slot description (if any)
    if (ps.expandedSlot >= 0 && ps.expandedSlot < 4) {
      const slotDef = EQUIPMENT_SLOTS[ps.expandedSlot];
      const item = state.player.equipment[slotDef.key];
      if (item?.equipmentData) {
        curY += 2;
        // Description card
        const descH = 28;
        const rarityColor = RARITY_COLORS[item.rarity] || "#aaa";
        drawRoundRect(ctx, x + margin, curY, innerW, descH, 4,
          "rgba(255,255,255,0.04)", adjustAlpha(rarityColor, 0.3));
        // Description text
        ctx.font = "6px sans-serif";
        ctx.fillStyle = "rgba(255,255,255,0.7)";
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(item.equipmentData.description, x + margin + 6, curY + 4, innerW - 12);
        // Stat summary
        const statParts: string[] = [];
        if (item.equipmentData.attackBonus) statParts.push(`ATK +${item.equipmentData.attackBonus}`);
        if (item.equipmentData.defenseBonus) statParts.push(`DEF +${item.equipmentData.defenseBonus}`);
        if (item.equipmentData.hpBonus) statParts.push(`HP +${item.equipmentData.hpBonus}`);
        ctx.font = "bold 5px sans-serif";
        ctx.fillStyle = rarityColor;
        ctx.fillText(statParts.join("  \u00B7  "), x + margin + 6, curY + 16, innerW - 12);
        curY += descH + 4;
      }
    }

    curY += 4;

    // Divider
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(x + margin, curY);
    ctx.lineTo(x + w - margin, curY);
    ctx.stroke();
    curY += 6;

    // ── Section: Skills ───────────────────────────────────────────

    drawSectionHeader(ctx, x + margin, curY, innerW, "SKILLS");
    curY += 14;

    const skills = state.player.skills;

    // ── Radar Chart ──────────────────────────────────────────────
    const radarCx = x + w / 2;
    const radarR = Math.min(w * 0.28, 42);
    const radarCy = curY + radarR + 2;
    const n = SKILLS.length;

    // Background rings
    for (let ring = 1; ring <= 4; ring++) {
      const r = radarR * (ring / 4);
      ctx.strokeStyle = `rgba(255,255,255,${ring === 4 ? 0.12 : 0.06})`;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      for (let j = 0; j <= n; j++) {
        const angle = (j / n) * Math.PI * 2 - Math.PI / 2;
        const px = radarCx + Math.cos(angle) * r;
        const py = radarCy + Math.sin(angle) * r;
        if (j === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();
    }

    // Axis lines + labels
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      const ex = radarCx + Math.cos(angle) * radarR;
      const ey = radarCy + Math.sin(angle) * radarR;

      ctx.strokeStyle = "rgba(255,255,255,0.08)";
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(radarCx, radarCy);
      ctx.lineTo(ex, ey);
      ctx.stroke();

      // Label
      const lx = radarCx + Math.cos(angle) * (radarR + 10);
      const ly = radarCy + Math.sin(angle) * (radarR + 10);
      ctx.font = "6px sans-serif";
      ctx.fillStyle = SKILLS[i].color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(SKILLS[i].label, lx, ly);
    }

    // Data polygon (filled)
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      const val = Math.min(100, skills[SKILLS[i].key] ?? 1) / 100;
      const r = radarR * Math.max(0.05, val);
      const px = radarCx + Math.cos(angle) * r;
      const py = radarCy + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fillStyle = "rgba(124,58,237,0.25)";
    ctx.fill();
    ctx.strokeStyle = "rgba(124,58,237,0.7)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Data points (dots)
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      const val = Math.min(100, skills[SKILLS[i].key] ?? 1) / 100;
      const r = radarR * Math.max(0.05, val);
      const px = radarCx + Math.cos(angle) * r;
      const py = radarCy + Math.sin(angle) * r;
      ctx.fillStyle = SKILLS[i].color;
      ctx.beginPath();
      ctx.arc(px, py, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    curY = radarCy + radarR + 18;

    // ── Skill Bars (below radar) ────────────────────────────────
    const barH = 6;
    const barSpacing = 18;
    const barMargin = 10;
    const barW = w - barMargin * 2 - 50;

    for (let i = 0; i < n; i++) {
      const by = curY + i * barSpacing;

      const skill = SKILLS[i];
      const rawXP = skills[skill.key] ?? 1;
      const level = levelFromXP(rawXP);
      const tier = masteryTier(level);
      const currentLevelXP = xpForLevel(level);
      const nextLevelXP = xpForLevel(level + 1);
      const progress = nextLevelXP > currentLevelXP
        ? (rawXP - currentLevelXP) / (nextLevelXP - currentLevelXP)
        : 1;

      // Label + level
      ctx.font = "bold 7px sans-serif";
      ctx.fillStyle = skill.color;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(`${skill.icon} ${skill.label}`, x + barMargin, by + barH / 2);

      // Level number (right)
      ctx.font = "bold 7px monospace";
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.textAlign = "right";
      ctx.fillText(`Lv${level}`, x + w - barMargin, by + barH / 2);

      // Progress bar
      const pbX = x + barMargin + 50;
      const pbW = barW;
      // Background
      ctx.fillStyle = "rgba(255,255,255,0.06)";
      drawRoundRect(ctx, pbX, by, pbW, barH, barH / 2);
      ctx.fill();
      // Fill
      if (progress > 0) {
        const fillW = Math.max(barH, pbW * progress);
        const grad = ctx.createLinearGradient(pbX, 0, pbX + fillW, 0);
        grad.addColorStop(0, skill.color);
        grad.addColorStop(1, adjustAlpha(skill.color, 0.6));
        ctx.fillStyle = grad;
        drawRoundRect(ctx, pbX, by, fillW, barH, barH / 2);
        ctx.fill();
      }

      // Tier badge (tiny, next to level)
      if (level >= 20) {
        ctx.font = "5px sans-serif";
        ctx.fillStyle = "rgba(255,255,255,0.35)";
        ctx.textAlign = "right";
        ctx.fillText(tier, x + w - barMargin, by + barH / 2 + 8);
      }
    }

    curY += n * barSpacing + 6;

    // ── Total Consciousness Score ───────────────────────────────
    const totalXP = SKILLS.reduce((sum, s) => sum + (skills[s.key] ?? 0), 0);
    const totalLevel = SKILLS.reduce((sum, s) => sum + levelFromXP(skills[s.key] ?? 0), 0);

    ctx.font = "bold 8px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`\u2764 Consciousness: ${totalLevel} \u00B7 ${totalXP} XP`, x + w / 2, curY);
    curY += 16;

    // ── Update max scroll ──
    const totalContentH = curY + ps.scrollY - y;
    ps.maxScrollY = Math.max(0, totalContentH - h);

    // ── Scroll indicators ──
    if (ps.scrollY > 1) {
      // Top fade
      const fadeGrad = ctx.createLinearGradient(x, y, x, y + 10);
      fadeGrad.addColorStop(0, "rgba(28,25,23,0.8)");
      fadeGrad.addColorStop(1, "rgba(28,25,23,0)");
      ctx.fillStyle = fadeGrad;
      ctx.fillRect(x, y, w, 10);
    }
    if (ps.scrollY < ps.maxScrollY - 1) {
      // Bottom fade
      const fadeGrad = ctx.createLinearGradient(x, y + h - 10, x, y + h);
      fadeGrad.addColorStop(0, "rgba(28,25,23,0)");
      fadeGrad.addColorStop(1, "rgba(28,25,23,0.8)");
      ctx.fillStyle = fadeGrad;
      ctx.fillRect(x, y + h - 10, w, 10);
    }

    ctx.restore();
  },

  handleTap(localX, localY, state) {
    const ps = getState(state);
    // Adjust tap Y for scroll
    const adjustedY = localY + ps.scrollY;

    // Hit-test equipment slots — compute their positions
    // The equipment grid starts after combat section
    // We need to reconstruct the layout Y positions
    const margin = 8;
    let curY = 4;
    curY += 2 + 16; // section header
    curY += 32 + 6; // level badge + bars
    curY += 16;     // attack row
    curY += 16 + 4; // defense row
    curY += 6;      // divider gap
    curY += 16;     // equipment section header

    const slotH = 36;
    const slotGap = 6;
    const innerW = 172; // approximate — actual depends on rect.w but close enough for hit testing

    for (let i = 0; i < 4; i++) {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const slotSize = Math.floor((innerW - 6) / 2);
      const sx = margin + col * (slotSize + slotGap);
      const sy = curY + row * (slotH + slotGap);

      if (localX >= sx && localX <= sx + slotSize &&
          adjustedY >= sy && adjustedY <= sy + slotH) {
        const item = state.player.equipment[EQUIPMENT_SLOTS[i].key];
        if (item?.equipmentData) {
          // Toggle expanded description
          ps.expandedSlot = ps.expandedSlot === i ? -1 : i;
        } else {
          ps.expandedSlot = -1;
        }
        return true;
      }
    }

    // Tap elsewhere closes expanded slot
    if (ps.expandedSlot >= 0) {
      ps.expandedSlot = -1;
      return true;
    }

    return false;
  },

  handleDrag(dy, state) {
    const ps = getState(state);
    ps.scrollY = Math.max(0, Math.min(ps.scrollY + dy, ps.maxScrollY));
    ps.scrollVelocity = 0;
  },

  handleDragEnd(velocityY, state) {
    const ps = getState(state);
    ps.scrollVelocity = velocityY;
  },

  onOpen(state) {
    const ps = getState(state);
    ps.scrollY = 0;
    ps.scrollVelocity = 0;
    ps.expandedSlot = -1;
  },
};

// ─── Drawing Helpers ────────────────────────────────────────────────────────

// ─── Living Combat Section ─────────────────────────────────────────────
// Renders momentum, flow, fatigue, combat style, species familiarity, and
// weapon attunement inside the Pulse app. Returns the updated Y cursor.

const ATTUNEMENT_COLORS: Record<string, string> = {
  fresh:     "#706050",
  familiar:  "#88a0c0",
  bonded:    "#88c088",
  attuned:   "#d4a030",
  soulbound: "#ff8844",
};

function drawLivingCombatSection(
  ctx: CanvasRenderingContext2D,
  x: number,
  startY: number,
  w: number,
  state: GameState,
): number {
  let curY = startY;

  drawSectionHeader(ctx, x, curY, w, "LIVING COMBAT");
  curY += 16;

  const momentum = getMomentumState();
  const familiarity = getFamiliarity();
  const attunement = getAttunementData();
  const awareness = getCombatAwareness(state);

  // ── Momentum strip + flow/fatigue ─────────────────────────────
  // Three stacked mini-bars: momentum (gold), flow threshold marker,
  // fatigue (dim red).
  const stripH = 6;
  const stripGap = 2;
  const labelW = 40;
  const barW = w - labelW - 4;
  const barX = x + labelW;

  // Momentum bar
  ctx.font = "bold 6px sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.fillText("Momentum", x + 2, curY + stripH / 2);

  ctx.fillStyle = "rgba(255,255,255,0.06)";
  drawRoundRect(ctx, barX, curY, barW, stripH, stripH / 2);
  ctx.fill();
  if (momentum.value > 0.01) {
    const fw = Math.max(stripH, barW * momentum.value);
    const grad = ctx.createLinearGradient(barX, 0, barX + fw, 0);
    grad.addColorStop(0, "#88b8e0");
    grad.addColorStop(0.5, "#fbbf24");
    grad.addColorStop(1, momentum.inFlow ? "#ffe890" : "#e87820");
    ctx.fillStyle = grad;
    drawRoundRect(ctx, barX, curY, fw, stripH, stripH / 2);
    ctx.fill();
  }
  // Flow threshold marker at 0.65
  const flowX = barX + barW * 0.65;
  ctx.fillStyle = momentum.inFlow ? "#ffe890" : "rgba(255,204,68,0.6)";
  ctx.fillRect(flowX - 0.5, curY - 1, 1, stripH + 2);
  // Peak marker
  if (momentum.peak > 0.05) {
    const peakX = barX + barW * momentum.peak;
    ctx.fillStyle = "#ffeeaa";
    ctx.beginPath();
    ctx.arc(peakX, curY + stripH / 2, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }
  // Numeric value at the right
  ctx.font = "bold 5px monospace";
  ctx.fillStyle = momentum.inFlow ? "#ffe890" : "rgba(255,255,255,0.65)";
  ctx.textAlign = "right";
  ctx.fillText(`${(momentum.value * 100).toFixed(0)}%`, x + w - 2, curY + stripH / 2);
  curY += stripH + stripGap;

  // Fatigue bar
  ctx.font = "bold 6px sans-serif";
  ctx.textAlign = "left";
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.fillText("Fatigue", x + 2, curY + stripH / 2);

  ctx.fillStyle = "rgba(255,255,255,0.06)";
  drawRoundRect(ctx, barX, curY, barW, stripH, stripH / 2);
  ctx.fill();
  if (momentum.fatigue > 0.01) {
    const fw = Math.max(stripH, barW * momentum.fatigue);
    ctx.fillStyle = "rgba(200,80,60,0.55)";
    drawRoundRect(ctx, barX, curY, fw, stripH, stripH / 2);
    ctx.fill();
  }
  ctx.font = "bold 5px monospace";
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.textAlign = "right";
  ctx.fillText(`${(momentum.fatigue * 100).toFixed(0)}%`, x + w - 2, curY + stripH / 2);
  curY += stripH + stripGap + 2;

  // Flow state indicator
  if (momentum.inFlow) {
    ctx.font = "bold 7px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#ffe890";
    ctx.fillText("\u25C6 FLOW STATE \u25C6", x + w / 2, curY);
    curY += 10;
  }

  // ── Combat Style Radar ────────────────────────────────────────
  // Aggression / Adaptability / Precision triangle
  const style = awareness.style;
  const radarY = curY + 2;
  const radarR = 28;
  const radarCx = x + radarR + 6;
  const radarCy = radarY + radarR;

  // Background triangle rings
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 0.5;
  for (let ring = 1; ring <= 3; ring++) {
    const r = radarR * (ring / 3);
    ctx.beginPath();
    for (let j = 0; j < 3; j++) {
      const a = (j / 3) * Math.PI * 2 - Math.PI / 2;
      const px = radarCx + Math.cos(a) * r;
      const py = radarCy + Math.sin(a) * r;
      if (j === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.stroke();
  }

  // Axis lines + labels
  const axes = [
    { label: "AGG", value: style.aggression, color: "#e87820" },
    { label: "ADP", value: style.adaptability, color: "#88c088" },
    { label: "PRE", value: style.precision, color: "#88b8e0" },
  ];
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 - Math.PI / 2;
    const ex = radarCx + Math.cos(a) * radarR;
    const ey = radarCy + Math.sin(a) * radarR;
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.beginPath();
    ctx.moveTo(radarCx, radarCy);
    ctx.lineTo(ex, ey);
    ctx.stroke();
    // Label
    const lx = radarCx + Math.cos(a) * (radarR + 6);
    const ly = radarCy + Math.sin(a) * (radarR + 6);
    ctx.font = "bold 5px sans-serif";
    ctx.fillStyle = axes[i].color;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(axes[i].label, lx, ly);
  }

  // Data polygon
  ctx.beginPath();
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 - Math.PI / 2;
    const r = radarR * Math.max(0.05, axes[i].value);
    const px = radarCx + Math.cos(a) * r;
    const py = radarCy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fillStyle = "rgba(212,160,48,0.2)";
  ctx.fill();
  ctx.strokeStyle = "rgba(212,160,48,0.75)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Data points
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 - Math.PI / 2;
    const r = radarR * Math.max(0.05, axes[i].value);
    ctx.fillStyle = axes[i].color;
    ctx.beginPath();
    ctx.arc(radarCx + Math.cos(a) * r, radarCy + Math.sin(a) * r, 1.8, 0, Math.PI * 2);
    ctx.fill();
  }

  // ── Right column: familiarity + outcome ───────────────────────
  const rightX = radarCx + radarR + 12;
  const rightY = radarY;
  const rightW = w - (rightX - x);

  // Combat breadth
  ctx.font = "bold 7px sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.fillText("Breadth", rightX, rightY);
  ctx.font = "bold 11px sans-serif";
  ctx.fillStyle = "#fbbf24";
  ctx.fillText(`${familiarity.uniqueSpeciesDefeated}`, rightX, rightY + 9);
  ctx.font = "5px sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.fillText("species known", rightX + 14, rightY + 14);

  // Kill streak
  ctx.font = "bold 7px sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.fillText("Streak", rightX, rightY + 24);
  const streak = state.player.combatState.sessionStats.killStreak;
  ctx.font = "bold 11px sans-serif";
  ctx.fillStyle = streak >= 3 ? "#ff8844" : "#fbbf24";
  ctx.fillText(`${streak}`, rightX, rightY + 33);
  ctx.font = "5px sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.fillText("current", rightX + 14, rightY + 38);

  // Recent outcome
  if (awareness.recentOutcome) {
    const ageSec = (performance.now() / 1000) - awareness.outcomeAt;
    if (ageSec < 60) {
      const outcomeColor: Record<string, string> = {
        victory:        "#60e060",
        defeat:         "#e04040",
        retreat:        "#e0a040",
        narrow_escape:  "#e0c060",
      };
      ctx.font = "bold 6px sans-serif";
      ctx.fillStyle = outcomeColor[awareness.recentOutcome] ?? "#ffffff";
      ctx.fillText(awareness.recentOutcome.replace("_", " ").toUpperCase(), rightX, rightY + 48);
    }
  }

  curY = radarCy + radarR + 6;

  // ── Weapon Attunement List ────────────────────────────────────
  // List up to 3 most-bonded weapons with tier colored rows.
  const weaponEntries = Object.entries(attunement.weapons)
    .sort(([, a], [, b]) => b.level - a.level)
    .slice(0, 3);

  if (weaponEntries.length > 0) {
    ctx.font = "bold 6px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText("ATTUNEMENT", x + 2, curY + 4);
    curY += 10;

    const rowH = 10;
    for (const [name, wa] of weaponEntries) {
      const rowColor = ATTUNEMENT_COLORS[wa.tier] ?? "#aaaaaa";

      // Row background
      ctx.fillStyle = "rgba(255,255,255,0.04)";
      drawRoundRect(ctx, x, curY, w, rowH, 2);
      ctx.fill();

      // Tier gem
      ctx.fillStyle = rowColor;
      ctx.beginPath();
      ctx.arc(x + 6, curY + rowH / 2, 2, 0, Math.PI * 2);
      ctx.fill();

      // Weapon name (truncated)
      ctx.font = "bold 6px sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      const maxNameW = w * 0.45;
      let displayName = name;
      while (ctx.measureText(displayName).width > maxNameW && displayName.length > 4) {
        displayName = displayName.slice(0, -1);
      }
      if (displayName !== name) displayName = displayName.slice(0, -1) + "\u2026";
      ctx.fillText(displayName, x + 12, curY + rowH / 2);

      // Tier label
      ctx.font = "bold 5px sans-serif";
      ctx.fillStyle = rowColor;
      ctx.textAlign = "left";
      const tierLabel = wa.tier.charAt(0).toUpperCase() + wa.tier.slice(1);
      ctx.fillText(tierLabel, x + 12 + maxNameW + 4, curY + rowH / 2);

      // Level progress bar (small, right side)
      const pbW = w * 0.2;
      const pbX = x + w - pbW - 24;
      const pbY = curY + rowH / 2 - 1.5;
      ctx.fillStyle = "rgba(255,255,255,0.08)";
      drawRoundRect(ctx, pbX, pbY, pbW, 3, 1.5);
      ctx.fill();
      if (wa.level > 0.02) {
        ctx.fillStyle = rowColor;
        drawRoundRect(ctx, pbX, pbY, Math.max(3, pbW * wa.level), 3, 1.5);
        ctx.fill();
      }

      // Percent text
      ctx.font = "bold 5px monospace";
      ctx.fillStyle = "rgba(255,255,255,0.55)";
      ctx.textAlign = "right";
      ctx.fillText(`${Math.round(wa.level * 100)}%`, x + w - 2, curY + rowH / 2);

      curY += rowH + 2;
    }
  } else {
    ctx.font = "italic 6px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("no weapons attuned yet", x + w / 2, curY + 6);
    curY += 14;
  }

  return curY + 4;
}

/** Draw a warm gold section header with decorative line */
function drawSectionHeader(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number,
  label: string
) {
  // Gold header text
  ctx.font = "bold 7px sans-serif";
  ctx.fillStyle = "#c8a030";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(label, x + 2, y);

  // Decorative line after text
  const textW = ctx.measureText(label).width;
  ctx.strokeStyle = "rgba(200,160,48,0.25)";
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(x + textW + 8, y + 4);
  ctx.lineTo(x + w, y + 4);
  ctx.stroke();
}

/** Draw a stat row like "Attack: 5 (+3) = 8" */
function drawStatRow(
  ctx: CanvasRenderingContext2D,
  labelX: number, y: number, rightX: number,
  icon: string, label: string, base: number, bonus: number, accentColor: string
) {
  const cy = y + 7;

  // Icon + Label
  ctx.font = "7px sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(`${label}`, labelX, cy);

  // Values on right
  ctx.textAlign = "right";
  const total = base + bonus;

  if (bonus > 0) {
    // Total in bold gold
    ctx.font = "bold 8px sans-serif";
    ctx.fillStyle = accentColor;
    ctx.fillText(`${total}`, rightX, cy);

    // Bonus in parens
    const totalW = ctx.measureText(`${total}`).width;
    ctx.font = "6px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.4)";
    ctx.fillText(`${base} (+${bonus}) = `, rightX - totalW - 2, cy);
  } else {
    ctx.font = "bold 8px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.fillText(`${base}`, rightX, cy);
  }
}

/** Draw an equipment slot cell — filled or empty */
function drawEquipSlot(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  slotDef: SlotDisplay, item: InventoryItem | null,
  expanded: boolean
) {
  if (item && item.equipmentData) {
    // Filled slot
    const rarityColor = RARITY_COLORS[item.rarity] || "#aaa";

    // Background card with rarity tint
    drawRoundRect(ctx, x, y, w, h, 4,
      adjustAlpha(rarityColor, 0.08),
      expanded ? rarityColor : adjustAlpha(rarityColor, 0.35));

    // Icon area (left side)
    const iconArea = 22;
    const iconCx = x + iconArea / 2 + 3;
    const iconCy = y + h / 2;

    ctx.strokeStyle = rarityColor;
    ctx.fillStyle = adjustAlpha(rarityColor, 0.3);
    drawEquipmentIcon(ctx, slotDef.key, iconCx, iconCy, iconArea, true);

    // Item name
    ctx.font = "bold 6px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    const nameX = x + iconArea + 6;
    const nameW = w - iconArea - 10;
    drawText(ctx, item.name, nameX, y + 6, nameW, "bold 6px sans-serif", "rgba(255,255,255,0.85)");

    // Short stat summary
    const statParts: string[] = [];
    if (item.equipmentData.attackBonus) statParts.push(`+${item.equipmentData.attackBonus} ATK`);
    if (item.equipmentData.defenseBonus) statParts.push(`+${item.equipmentData.defenseBonus} DEF`);
    if (item.equipmentData.hpBonus) statParts.push(`+${item.equipmentData.hpBonus} HP`);
    ctx.font = "5px sans-serif";
    ctx.fillStyle = adjustAlpha(rarityColor, 0.8);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(statParts.join(" \u00B7 "), nameX, y + 16, nameW);

    // Rarity label (bottom)
    const rarityLabel = item.rarity.charAt(0).toUpperCase() + item.rarity.slice(1);
    ctx.font = "4px sans-serif";
    ctx.fillStyle = adjustAlpha(rarityColor, 0.5);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(rarityLabel, nameX, y + 25);
  } else {
    // Empty slot — dashed outline
    drawRoundRect(ctx, x, y, w, h, 4);
    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = "rgba(255,255,255,0.12)";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.setLineDash([]);

    // Empty icon
    const iconCx = x + w / 2;
    const iconCy = y + h / 2 - 4;
    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.fillStyle = "rgba(255,255,255,0.08)";
    drawEquipmentIcon(ctx, slotDef.key, iconCx, iconCy, 20, false);

    // Slot label
    ctx.font = "5px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.2)";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(slotDef.label, x + w / 2, y + h - 10);
  }
}

// ─── Utility ────────────────────────────────────────────────────────────────

function adjustAlpha(hex: string, alpha: number): string {
  // Handle both #hex and rgba() formats
  if (hex.startsWith("rgba")) {
    // Replace alpha in existing rgba
    return hex.replace(/[\d.]+\)$/, `${alpha})`);
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ─── Register ─────────────────────────────────────────────────────────────

registerApp(pulseApp);

export { pulseApp };
